import { Dispatch, SetStateAction, useEffect, useMemo, useRef } from 'react';
import { AppNode, Edge, ProcessDefinition, Swimlane, NamedRuleSet } from '../types';
import { getNodeTypes, getDefaultData } from '../utils/nodeRegistry';
import { validateDiagram } from '../utils/validation';
import {
  createWorkflowAiCapability,
  EditorAiCapability,
  WorkflowModelAccess,
} from '../utils/aiCapability';

interface UseAiCapabilityArgs {
  nodes: AppNode[];
  edges: Edge[];
  setNodes: Dispatch<SetStateAction<AppNode[]>>;
  setEdges: Dispatch<SetStateAction<Edge[]>>;
  processes: ProcessDefinition[];
  activeProcessId: string;
  swimlanes: Swimlane[];
  ruleSets: NamedRuleSet[];
  onAiCapabilityReady?: (cap: EditorAiCapability | null) => void;
}

/**
 * Wires an {@link EditorAiCapability} to the live editor model and hands it to
 * the host via `onAiCapabilityReady`. The capability's tools route through the
 * editor's REAL mutation (setNodes/setEdges) and validation (validateDiagram)
 * so bad input is rejected with a reason rather than corrupting the model.
 *
 * State is read through refs so a single, stable capability object always sees
 * the latest nodes/edges — we therefore call `onAiCapabilityReady(cap)` exactly
 * once (on mount / when the callback identity changes), not on every render.
 */
export const useAiCapability = ({
  nodes,
  edges,
  setNodes,
  setEdges,
  processes,
  activeProcessId,
  swimlanes,
  ruleSets,
  onAiCapabilityReady,
}: UseAiCapabilityArgs): void => {
  // Latest-state refs so the stable capability sees fresh data on each invoke.
  const stateRef = useRef({ nodes, edges, processes, activeProcessId, swimlanes, ruleSets });
  stateRef.current = { nodes, edges, processes, activeProcessId, swimlanes, ruleSets };

  // Hold the host callback in a ref so the publish effect does NOT depend on it
  // directly. An inline host callback (new identity every host render) would
  // otherwise re-fire the unregister/register cycle on every render.
  const onReadyRef = useRef(onAiCapabilityReady);
  onReadyRef.current = onAiCapabilityReady;

  // Stable model-access bound to refs + real mutators. Built once.
  const capability = useMemo<EditorAiCapability>(() => {
    const access: WorkflowModelAccess = {
      getNodes: () => stateRef.current.nodes,
      getEdges: () => stateRef.current.edges,
      getNodeTypes: () => getNodeTypes(),
      getDefaultData: (type) => getDefaultData(type),
      applyNodes: (updater) => setNodes(updater),
      applyEdges: (updater) => setEdges(updater),
      // Validate a CANDIDATE diagram: splice the supplied nodes/edges into the
      // active process and run the editor's REAL validateDiagram over the whole
      // project (so cross-process + ruleset rules still fire). Other process
      // state (swimlanes) is read from the latest refs.
      validate: (candidateNodes, candidateEdges) => {
        const s = stateRef.current;
        const merged = s.processes.map((p) =>
          p.id === s.activeProcessId
            ? { ...p, nodes: candidateNodes, edges: candidateEdges, swimlanes: s.swimlanes }
            : p
        );
        return validateDiagram(merged, s.ruleSets);
      },
    };
    return createWorkflowAiCapability(access);
    // setNodes/setEdges from useNodesState/useEdgesState are stable.
  }, [setNodes, setEdges]);

  // Publish once when the (stable) capability is created; retract the SAME
  // capability on unmount. Reads the host callback through a ref so a changing
  // inline callback identity does not re-fire this effect every render.
  useEffect(() => {
    onReadyRef.current?.(capability);
    return () => onReadyRef.current?.(null);
  }, [capability]);
};
